/* ==========================================================================
   Quiz: "Las 7 emociones básicas" — lógica de la aplicación
   Sin dependencias externas de pago. Solo JS puro (ES6) + html2canvas/jsPDF
   (cargados vía CDN gratuito, únicamente para exportar el certificado a PDF).
   ========================================================================== */

(() => {
  'use strict';

  /* ------------------------------------------------------------------------
     1. DATOS DEL CUESTIONARIO
     Cada pregunta tiene un id fijo (no cambia aunque se reordene el examen),
     el texto, las 3 opciones, la letra correcta y una "emotion" usada solo
     para colorear la línea de progreso / leyenda (elemento decorativo que
     conecta el diseño con el contenido).
     ------------------------------------------------------------------------ */
  const QUESTIONS = [
    { id: 1, emotion: 'general', text: '¿Qué son las emociones según la guía?', options: { A: 'Pensamientos que aparecen al estudiar.', B: 'Respuestas naturales del cuerpo y la mente frente a lo que vivimos.', C: 'Enfermedades que afectan el comportamiento.' }, correct: 'B' },
    { id: 2, emotion: 'general', text: 'Según Paul Ekman, ¿cuántas emociones básicas universales existen?', options: { A: 'Cinco.', B: 'Seis.', C: 'Siete.' }, correct: 'C' },
    { id: 3, emotion: 'miedo', text: '¿Cuál de las siguientes emociones aparece cuando percibimos un peligro real o imaginario?', options: { A: 'Miedo.', B: 'Alegría.', C: 'Desprecio.' }, correct: 'A' },
    { id: 4, emotion: 'sorpresa', text: '¿Qué emoción sentimos ante algo inesperado o novedoso?', options: { A: 'Tristeza.', B: 'Sorpresa.', C: 'Asco.' }, correct: 'B' },
    { id: 5, emotion: 'alegria', text: '¿Qué emoción experimentamos ante situaciones positivas, satisfactorias o significativas?', options: { A: 'Alegría.', B: 'Enojo.', C: 'Miedo.' }, correct: 'A' },
    { id: 6, emotion: 'tristeza', text: '¿Qué emoción surge cuando sentimos pérdidas, decepciones o situaciones dolorosas?', options: { A: 'Desprecio.', B: 'Tristeza.', C: 'Sorpresa.' }, correct: 'B' },
    { id: 7, emotion: 'enojo', text: '¿Qué emoción aparece cuando alguien transgrede nuestros límites o valores?', options: { A: 'Asco.', B: 'Enojo.', C: 'Alegría.' }, correct: 'B' },
    { id: 8, emotion: 'asco', text: '¿Qué emoción sentimos ante algo que percibimos como repulsivo o contaminante?', options: { A: 'Miedo.', B: 'Asco.', C: 'Sorpresa.' }, correct: 'B' },
    { id: 9, emotion: 'desprecio', text: '¿Qué emoción sentimos cuando percibimos a alguien como inferior o indigno de respeto?', options: { A: 'Desprecio.', B: 'Tristeza.', C: 'Alegría.' }, correct: 'A' },
    { id: 10, emotion: 'miedo', text: '¿Cuál es una utilidad principal del miedo?', options: { A: 'Detectar amenazas y activar respuestas de protección.', B: 'Hacer amigos con facilidad.', C: 'Evitar expresar emociones.' }, correct: 'A' },
    { id: 11, emotion: 'sorpresa', text: '¿Cuál es una utilidad de la sorpresa?', options: { A: 'Hacer que olvidemos lo ocurrido.', B: 'Orientarnos rápidamente ante lo inesperado.', C: 'Evitar cualquier cambio.' }, correct: 'B' },
    { id: 12, emotion: 'alegria', text: '¿Qué favorece la alegría según la guía?', options: { A: 'El aislamiento.', B: 'La conexión y el fortalecimiento de vínculos.', C: 'El rechazo hacia otras personas.' }, correct: 'B' },
    { id: 13, emotion: 'tristeza', text: '¿Qué nos ayuda a hacer la tristeza?', options: { A: 'Procesar lo que duele y pedir apoyo.', B: 'Ignorar nuestras emociones.', C: 'Reaccionar con agresividad.' }, correct: 'A' },
    { id: 14, emotion: 'enojo', text: '¿Qué nos permite hacer el enojo de manera saludable?', options: { A: 'Actuar, expresar lo que sentimos y buscar soluciones.', B: 'Evitar cualquier conflicto.', C: 'Escondernos de los problemas.' }, correct: 'A' },
    { id: 15, emotion: 'asco', text: '¿Cuál es la función principal del asco?', options: { A: 'Buscar nuevas experiencias.', B: 'Evitar aquello que puede ser dañino para nuestra salud.', C: 'Generar confianza con otras personas.' }, correct: 'B' },
    { id: 16, emotion: 'desprecio', text: '¿Qué nos ayuda a hacer el desprecio?', options: { A: 'Establecer límites y mantener nuestros valores.', B: 'Detectar peligros físicos.', C: 'Compartir emociones positivas.' }, correct: 'A' },
    { id: 17, emotion: 'miedo', text: 'Evolutivamente, ¿para qué ha servido el miedo?', options: { A: 'Para mejorar únicamente el rendimiento académico.', B: 'Para favorecer la supervivencia y anticiparnos al peligro.', C: 'Para evitar cualquier emoción.' }, correct: 'B' },
    { id: 18, emotion: 'sorpresa', text: 'Según la guía, ¿qué permitió la sorpresa desde el punto de vista evolutivo?', options: { A: 'Adaptarnos mejor a los cambios del entorno.', B: 'Permanecer siempre en el mismo lugar.', C: 'Evitar aprender cosas nuevas.' }, correct: 'A' },
    { id: 19, emotion: 'tristeza', text: '¿Cuál de las siguientes afirmaciones resume mejor la tristeza?', options: { A: 'Es una respuesta humana que permite sanar, crecer y reconectarnos con lo importante.', B: 'Es una emoción que debe evitarse siempre.', C: 'Solo aparece cuando una persona fracasa.' }, correct: 'A' },
    { id: 20, emotion: 'general', text: '¿Qué tienen en común las siete emociones básicas?', options: { A: 'Todas cumplen una función importante para ayudarnos a adaptarnos y comprender nuestras necesidades.', B: 'Todas son emociones negativas que deben eliminarse.', C: 'Solo sirven para reaccionar ante peligros.' }, correct: 'A' },
    { id: 21, emotion: 'miedo', text: '¿Qué característica facial es propia de la emoción de miedo?', options: { A: 'Cejas elevadas y juntas, ojos muy abiertos y labios ligeramente estirados.', B: 'Labios presionados y mandíbula tensa.', C: 'Comisuras labiales elevadas y mejillas levantadas.' }, correct: 'A' },
    { id: 22, emotion: 'alegria', text: '¿Cuál de las siguientes expresiones faciales corresponde a la alegría?', options: { A: 'Cejas fruncidas y mirada fija.', B: 'Sonrisa con comisuras elevadas y mejillas levantadas, con arrugas alrededor de los ojos.', C: 'Labio superior levantado y nariz arrugada.' }, correct: 'B' },
    { id: 23, emotion: 'tristeza', text: '¿Qué rasgo facial caracteriza la tristeza?', options: { A: 'Comisuras labiales caídas y párpados superiores descendidos.', B: 'Ojos muy abiertos y cejas arqueadas.', C: 'Labios estirados horizontalmente.' }, correct: 'A' },
    { id: 24, emotion: 'enojo', text: '¿Cómo suele manifestarse el enojo en el rostro?', options: { A: 'Cejas descendidas y contraídas, mirada intensa y mandíbula tensa.', B: 'Sonrisa amplia y ojos entrecerrados.', C: 'Labios relajados y mejillas caídas.' }, correct: 'A' },
    { id: 25, emotion: 'asco', text: '¿Qué expresión facial es característica del asco?', options: { A: 'Nariz arrugada y labio superior levantado.', B: 'Cejas elevadas y ojos muy abiertos.', C: 'Comisuras de la boca hacia abajo.' }, correct: 'A' },
    { id: 26, emotion: 'sorpresa', text: '¿Qué rasgo facial identifica mejor la sorpresa?', options: { A: 'Ojos muy abiertos, cejas elevadas y boca abierta.', B: 'Labios presionados y mandíbula contraída.', C: 'Nariz arrugada y labio superior elevado.' }, correct: 'A' },
    { id: 27, emotion: 'desprecio', text: '¿Cuál de las siguientes manifestaciones faciales corresponde al desprecio?', options: { A: 'Una sola comisura de la boca se eleva de forma unilateral.', B: 'Ambas comisuras de la boca descienden.', C: 'La boca permanece completamente abierta.' }, correct: 'A' },
    { id: 28, emotion: 'enojo', text: '¿Qué emoción suele presentar una mirada fija e intensa acompañada de tensión en el párpado inferior?', options: { A: 'Alegría.', B: 'Enojo.', C: 'Sorpresa.' }, correct: 'B' },
    { id: 29, emotion: 'tristeza', text: '¿Qué emoción suele acompañarse de una disminución del tono muscular facial y mejillas laxas?', options: { A: 'Tristeza.', B: 'Asco.', C: 'Desprecio.' }, correct: 'A' },
    { id: 30, emotion: 'sorpresa', text: '¿Qué emoción se caracteriza por mostrar los ojos muy abiertos para captar rápidamente información del entorno?', options: { A: 'Sorpresa.', B: 'Alegría.', C: 'Desprecio.' }, correct: 'A' }
  ];

  const TOTAL_QUESTIONS = QUESTIONS.length;
  const PASSING_SCORE = 15; // Puntaje mínimo para poder obtener el certificado

  // Etiquetas legibles para cada "emotion" (usadas en la leyenda decorativa)
  const EMOTION_LABELS = {
    miedo: 'Miedo', sorpresa: 'Sorpresa', alegria: 'Alegría', tristeza: 'Tristeza',
    enojo: 'Enojo', asco: 'Asco', desprecio: 'Desprecio', general: 'General'
  };

  // Claves de localStorage (namespaced para no chocar con otras apps del tema)
  const LS_RESULT_KEY = 'qe_resultado_v1';
  const LS_CERT_KEY = 'qe_certificado_v1';

  /* ------------------------------------------------------------------------
     2. ESTADO DE LA APLICACIÓN
     ------------------------------------------------------------------------ */
  let shuffledQuestions = [];
  let currentIndex = 0;
  let answers = {}; // { [questionId]: 'A' | 'B' | 'C' }

  /* ------------------------------------------------------------------------
     3. UTILIDADES
     ------------------------------------------------------------------------ */

  // Fisher-Yates: reordena las preguntas manteniendo cada objeto intacto,
  // por lo que las opciones y la respuesta correcta siguen asociadas a su
  // pregunta sin importar el nuevo orden.
  function shuffleArray(array) {
    const arr = array.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function getClassification(score) {
    if (score === 30) return { label: 'Excelente', icon: '🏆' };
    if (score >= 25) return { label: 'Muy bueno', icon: '🥇' };
    if (score >= 20) return { label: 'Bueno', icon: '👍' };
    if (score >= 15) return { label: 'Aceptable', icon: '🙂' };
    return { label: 'Debe repetir la prueba', icon: '❌' };
  }

  function formatDate(date) {
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function qs(selector, root = document) {
    return root.querySelector(selector);
  }

  /* ------------------------------------------------------------------------
     4. REFERENCIAS AL DOM
     ------------------------------------------------------------------------ */
  const app = qs('#quizApp');
  if (!app) return; // Si la sección no está en la página, no hacemos nada.

  const screens = {
    intro: qs('[data-screen="intro"]', app),
    quiz: qs('[data-screen="quiz"]', app),
    results: qs('[data-screen="results"]', app)
  };

  const btnStart = qs('#btnStart', app);
  const progressLabel = qs('#progressLabel', app);
  const progressFill = qs('#progressFill', app);
  const emotionLegend = qs('#emotionLegend', app);
  const questionText = qs('#questionText', app);
  const optionsContainer = qs('#optionsContainer', app);
  const btnPrev = qs('#btnPrev', app);
  const btnNext = qs('#btnNext', app);

  const scoreDisplay = qs('#scoreDisplay', app);
  const classificationDisplay = qs('#classificationDisplay', app);
  const certificateActions = qs('#certificateActions', app);
  const reviewContainer = qs('#reviewContainer', app);

  const certModal = qs('#certModal', app);
  const btnCloseModal = qs('#btnCloseModal', app);
  const certNameInput = qs('#certName', app);
  const btnGenerateCert = qs('#btnGenerateCert', app);
  const certificateTemplate = qs('#certificateTemplate', app);

  /* ------------------------------------------------------------------------
     5. NAVEGACIÓN ENTRE PANTALLAS
     ------------------------------------------------------------------------ */
  function showScreen(name) {
    Object.entries(screens).forEach(([key, el]) => {
      if (!el) return;
      el.classList.toggle('is-active', key === name);
    });
  }

  /* ------------------------------------------------------------------------
     6. LEYENDA DE EMOCIONES (elemento decorativo/temático)
     ------------------------------------------------------------------------ */
  function renderEmotionLegend(activeEmotion) {
    if (!emotionLegend) return;
    const emotions = ['miedo', 'sorpresa', 'alegria', 'tristeza', 'enojo', 'asco', 'desprecio'];
    emotionLegend.innerHTML = emotions.map((key) => {
      const isActive = key === activeEmotion;
      return `<span class="qe-dot qe-dot--${key}${isActive ? ' is-active' : ''}" title="${EMOTION_LABELS[key]}"></span>`;
    }).join('');
  }

  /* ------------------------------------------------------------------------
     7. RENDER DE LA PREGUNTA ACTUAL
     ------------------------------------------------------------------------ */
  function renderQuestion() {
    const q = shuffledQuestions[currentIndex];
    const percent = Math.round(((currentIndex + 1) / TOTAL_QUESTIONS) * 100);

    progressLabel.textContent = `Pregunta ${currentIndex + 1} de ${TOTAL_QUESTIONS} · ${percent}%`;
    progressFill.style.width = `${percent}%`;
    renderEmotionLegend(q.emotion);

    questionText.textContent = q.text;

    const selected = answers[q.id];
    optionsContainer.innerHTML = '';
    ['A', 'B', 'C'].forEach((letter) => {
      const optionId = `q${q.id}_${letter}`;
      const label = document.createElement('label');
      label.className = 'qe-option' + (selected === letter ? ' is-selected' : '');
      label.setAttribute('for', optionId);
      label.innerHTML = `
        <input type="radio" name="q${q.id}" id="${optionId}" value="${letter}" ${selected === letter ? 'checked' : ''}>
        <span class="qe-option__bullet"></span>
        <span class="qe-option__text"><strong>${letter})</strong> ${q.options[letter]}</span>
      `;
      label.addEventListener('click', () => {
        answers[q.id] = letter;
        renderQuestion(); // re-render para reflejar la selección y habilitar "Siguiente"
      });
      optionsContainer.appendChild(label);
    });

    btnPrev.disabled = currentIndex === 0;
    btnNext.textContent = currentIndex === TOTAL_QUESTIONS - 1 ? 'Finalizar' : 'Siguiente';
    btnNext.disabled = !answers[q.id]; // Bloqueado hasta responder la pregunta actual
  }

  function goNext() {
    const q = shuffledQuestions[currentIndex];
    if (!answers[q.id]) return; // Seguridad extra: no avanzar sin responder

    if (currentIndex === TOTAL_QUESTIONS - 1) {
      finishQuiz();
      return;
    }
    currentIndex++;
    renderQuestion();
  }

  function goPrev() {
    if (currentIndex === 0) return;
    currentIndex--;
    renderQuestion();
  }

  /* ------------------------------------------------------------------------
     8. INICIO Y FINALIZACIÓN DEL CUESTIONARIO
     ------------------------------------------------------------------------ */
  function startQuiz() {
    shuffledQuestions = shuffleArray(QUESTIONS);
    currentIndex = 0;
    answers = {};
    showScreen('quiz');
    renderQuestion();
  }

  function calculateScore() {
    let score = 0;
    shuffledQuestions.forEach((q) => {
      if (answers[q.id] === q.correct) score++;
    });
    return score;
  }

  function finishQuiz() {
    const score = calculateScore();
    const classification = getClassification(score);
    const passed = score >= PASSING_SCORE;

    // Guardamos el resultado "oficial" de este intento. El formulario de
    // certificado siempre va a leer ESTE registro (no un valor editable
    // por el usuario), evitando que se genere un certificado con un
    // puntaje distinto al realmente obtenido.
    const resultRecord = {
      score,
      total: TOTAL_QUESTIONS,
      classificationLabel: classification.label,
      classificationIcon: classification.icon,
      passed,
      timestamp: Date.now()
    };
    localStorage.setItem(LS_RESULT_KEY, JSON.stringify(resultRecord));

    // Cada intento nuevo invalida cualquier certificado emitido anteriormente.
    localStorage.removeItem(LS_CERT_KEY);

    renderResults(resultRecord);
    showScreen('results');
  }

  /* ------------------------------------------------------------------------
     9. PANTALLA DE RESULTADOS
     ------------------------------------------------------------------------ */
  function renderResults(result) {
    scoreDisplay.textContent = `Has obtenido: ${result.score} / ${result.total}`;
    classificationDisplay.innerHTML = `${result.classificationIcon} <span>${result.classificationLabel}</span>`;
    classificationDisplay.className = 'qe-classification ' + (result.passed ? 'is-passed' : 'is-failed');

    renderCertificateActions(result);
    renderReview();
  }

  function renderCertificateActions(result) {
    const existingCert = getStoredCertificate();

    if (!result.passed) {
      // Menos de 15 puntos: solo mensaje + botón para reintentar.
      certificateActions.innerHTML = `
        <p class="qe-retry-msg">Debe repetir la prueba para poder obtener el certificado.</p>
        <button class="qe-btn qe-btn--primary" id="btnRetry">Realizar nuevamente la evaluación</button>
      `;
      qs('#btnRetry', certificateActions).addEventListener('click', resetAndRetry);
      return;
    }

    if (existingCert) {
      // Ya se generó un certificado para este intento: no permitir cambiar
      // el nombre. Solo se puede volver a descargar el mismo PDF o reintentar
      // el examen (lo que invalida este certificado y exige un nuevo intento).
      certificateActions.innerHTML = `
        <p class="qe-cert-issued">Certificado emitido a nombre de <strong>${escapeHtml(existingCert.name)}</strong>.</p>
        <button class="qe-btn qe-btn--primary" id="btnRedownload">Descargar certificado (PDF)</button>
        <button class="qe-btn qe-btn--ghost" id="btnRetry2">Realizar nuevamente la evaluación</button>
      `;
      qs('#btnRedownload', certificateActions).addEventListener('click', () => downloadCertificate(existingCert));
      qs('#btnRetry2', certificateActions).addEventListener('click', resetAndRetry);
      return;
    }

    certificateActions.innerHTML = `
      <button class="qe-btn qe-btn--gold" id="btnOpenCert">Obtener certificado</button>
    `;
    qs('#btnOpenCert', certificateActions).addEventListener('click', openCertModal);
  }

  function resetAndRetry() {
    localStorage.removeItem(LS_RESULT_KEY);
    localStorage.removeItem(LS_CERT_KEY);
    showScreen('intro');
  }

  function renderReview() {
    reviewContainer.innerHTML = '';
    shuffledQuestions.forEach((q, idx) => {
      const userLetter = answers[q.id];
      const isCorrect = userLetter === q.correct;
      const item = document.createElement('div');
      item.className = 'qe-review-item';
      item.innerHTML = `
        <p class="qe-review-number">Pregunta ${idx + 1}</p>
        <p class="qe-review-question">${q.text}</p>
        <p class="qe-review-answer ${isCorrect ? 'is-correct' : 'is-incorrect'}">
          Tu respuesta: ${userLetter}) ${q.options[userLetter]} ${isCorrect ? '✅ Correcta' : '❌ Incorrecta'}
        </p>
        ${!isCorrect ? `<p class="qe-review-correct">Respuesta correcta: ${q.correct}) ${q.options[q.correct]}</p>` : ''}
      `;
      reviewContainer.appendChild(item);
    });
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ------------------------------------------------------------------------
     10. MODAL Y GENERACIÓN DE CERTIFICADO
     ------------------------------------------------------------------------ */
  function openCertModal() {
    certNameInput.value = '';
    btnGenerateCert.disabled = true;
    certModal.classList.add('is-open');
    certNameInput.focus();
  }

  function closeCertModal() {
    certModal.classList.remove('is-open');
  }

  function getStoredResult() {
    try {
      const raw = localStorage.getItem(LS_RESULT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function getStoredCertificate() {
    try {
      const raw = localStorage.getItem(LS_CERT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  async function generateCertificate() {
    const name = certNameInput.value.trim();
    if (!name) return;

    const result = getStoredResult();
    if (!result || !result.passed) {
      // Defensa extra: si por algún motivo no hay un resultado aprobado
      // guardado, no se genera nada.
      closeCertModal();
      return;
    }

    const certRecord = {
      name,
      score: result.score,
      total: result.total,
      classificationLabel: result.classificationLabel,
      date: formatDate(new Date()),
      issuedAt: Date.now()
    };
    localStorage.setItem(LS_CERT_KEY, JSON.stringify(certRecord));

    closeCertModal();
    await downloadCertificate(certRecord);
    renderCertificateActions(result);
  }

  function fitCertificateName(nameEl) {
    // Vuelve al tamaño base y va reduciendo de a 1px hasta que el nombre
    // entre en el ancho disponible (evita que nombres largos se salgan
    // del panel del certificado).
    const maxFontSize = 30;
    const minFontSize = 15;
    nameEl.style.fontSize = maxFontSize + 'px';
    let fontSize = maxFontSize;
    while (nameEl.scrollWidth > nameEl.clientWidth && fontSize > minFontSize) {
      fontSize -= 1;
      nameEl.style.fontSize = fontSize + 'px';
    }
  }

  function fillCertificateTemplate(certRecord) {
    const nameEl = qs('[data-cert-name]', certificateTemplate);
    nameEl.textContent = certRecord.name;
    fitCertificateName(nameEl);
    qs('[data-cert-meta]', certificateTemplate).textContent =
      `Puntaje ${certRecord.score}/${certRecord.total} · Calificación ${certRecord.classificationLabel.toUpperCase()} · ${certRecord.date}`;
  }

  async function downloadCertificate(certRecord) {
    fillCertificateTemplate(certRecord);

    if (typeof window.html2canvas === 'undefined' || typeof window.jspdf === 'undefined') {
      console.error('html2canvas o jsPDF no están disponibles.');
      return;
    }

    certificateTemplate.classList.add('is-rendering');
    try {
      const canvas = await window.html2canvas(certificateTemplate, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true
      });
      const imgData = canvas.toDataURL('image/png');

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);

      const safeName = certRecord.name.replace(/[^a-z0-9]+/gi, '_');
      pdf.save(`certificado_emociones_${safeName}.pdf`);
    } catch (err) {
      console.error('Error generando el certificado en PDF:', err);
    } finally {
      certificateTemplate.classList.remove('is-rendering');
    }
  }

  /* ------------------------------------------------------------------------
     11. EVENTOS
     ------------------------------------------------------------------------ */
  btnStart.addEventListener('click', startQuiz);
  btnNext.addEventListener('click', goNext);
  btnPrev.addEventListener('click', goPrev);
  btnCloseModal.addEventListener('click', closeCertModal);
  certModal.addEventListener('click', (e) => { if (e.target === certModal) closeCertModal(); });
  certNameInput.addEventListener('input', () => {
    btnGenerateCert.disabled = certNameInput.value.trim().length === 0;
  });
  btnGenerateCert.addEventListener('click', generateCertificate);

  /* ------------------------------------------------------------------------
     12. ESTADO INICIAL AL CARGAR LA PÁGINA
     Si el visitante ya tiene un resultado guardado (por ejemplo recargó la
     página después de terminar), se lo mostramos directamente en vez de
     obligarlo a repetir el examen.
     ------------------------------------------------------------------------ */
  function init() {
    const storedResult = getStoredResult();
    if (storedResult) {
      // No tenemos las 30 preguntas "shuffled" guardadas (por diseño, para no
      // sobrecargar localStorage), así que la revisión detallada solo está
      // disponible inmediatamente después de rendir. Si hay un resultado
      // previo, mostramos el resumen y las acciones de certificado, con la
      // opción de repetir la evaluación cuando quiera.
      shuffledQuestions = [];
      scoreDisplay.textContent = `Has obtenido: ${storedResult.score} / ${storedResult.total}`;
      classificationDisplay.innerHTML = `${storedResult.classificationIcon} <span>${storedResult.classificationLabel}</span>`;
      classificationDisplay.className = 'qe-classification ' + (storedResult.passed ? 'is-passed' : 'is-failed');
      renderCertificateActions(storedResult);
      reviewContainer.innerHTML = '<p class="qe-review-note">La revisión detallada solo está disponible justo después de finalizar la evaluación. Si querés verla de nuevo, realizá la evaluación otra vez.</p>';
      showScreen('results');
      return;
    }
    showScreen('intro');
  }

  init();
})();
